import { Component, OnInit, Inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormArray, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatRadioModule } from '@angular/material/radio';
import { Request, RequestDetail } from '../../_model/procurement/Request';
import { ProcurementGroup } from '../../_model/procurement/ProcurementGroup';
import { ProcurementItem } from '../../_model/procurement/ProcurementItem';
import { Vendor } from '../../_model/procurement/Vendor';
import { ProcurementService } from '../../_services/procurement.service';
import { LookupService } from '../../_services/lookup.service';
import { AuthenticationService } from '../../_services/authentication.service';
import { RejectReasonDialogComponent } from '../reject-reason-dialog/reject-reason-dialog.component';
import { PhoneMaskDirective } from '../../_helpers/phone-mask.directive';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-request-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatButtonToggleModule,
    MatAutocompleteModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatRadioModule,
    PhoneMaskDirective
  ],
  templateUrl: './request-dialog.component.html',
  styleUrls: ['./request-dialog.component.scss']
})
export class RequestDialogComponent implements OnInit {
  
  requestForm: FormGroup;
  pgGroups: ProcurementGroup[] = [];
  pgdGroups: ProcurementGroup[] = [];
  wgGroups: ProcurementGroup[] = [];
  allGroups: ProcurementGroup[] = [];
  procurementItems: ProcurementItem[] = [];
  vendorList: Vendor[] = [];
  departmentList: any[] = []; // Departman listesi
  projectList: any[] = []; // Proje listesi
  tenantList: any[] = []; // Kiracı listesi
  paymentTypeList: any[] = []; // Ödeme tipi (PO_PAYMENT_TYPE)
  creditCardList: any[] = []; // Kredi kartları (PO_CREDIT_CARDS)
  unitTypeList: any[] = []; // Birim tipi (UNIT_TYPE)
  recordType: 'REQUISITION' | 'EXPENSE' = 'REQUISITION'; // Toggle için
  vatRates = [0, 1, 10, 20]; // KDV oranları
  showOtherVendorFields = false; // Diğer satıcı alanlarını göster/gizle
  
  displayedColumns: string[] = ['itemCode', 'itemDesc', 'brand', 'quantity', 'unit', 'location', 'tenant', 'actions'];
  dataSource: any[] = [];

  isReadOnly = false; // Readonly mode kontrolü
  selectedVendor: Vendor | null = null; // Readonly mode'da vendor göstermek için
  showApproveReject = false; // Onay/Red butonlarını göster/gizle

  // Talep Başlığı AI validasyonu - ikinci kayıtta kontrol atlanır
  headerValidationSuggestion: string | null = null;
  skipHeaderValidation = false;

  constructor(
    private fb: FormBuilder,
    private procurementService: ProcurementService,
    private lookupService: LookupService,
    private authService: AuthenticationService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    public dialogRef: MatDialogRef<RequestDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { request?: Request; isReadOnly?: boolean; showApproveReject?: boolean }
  ) {
    this.requestForm = this.fb.group({
      type: ['REQUISITION', Validators.required],
      requestHeader: [''], // Talep Başlığı
      explanation: [''], // Talep Açıklaması
      invoiceDate: [''], // Fatura Tarihi (MASRAF için zorunlu)
      pgCode: ['', Validators.required],
      pgdCode: ['', Validators.required],
      wgCode: ['', Validators.required],
      pgCodeDesc: [''],
      pgdCodeDesc: [''],
      wgCodeDesc: [''],
      vendorId: [null],
      vendorSearch: [''],
      otherVendorTaxNumber: [''],
      otherVendorName: [''],
      // TALEP için Satıcı Önerisi alanları
      isVendorDefined: [true], // Satıcı tanımlı mı? (true/false)
      otherVendorPhone: [''], // Telefon
      otherVendorRelatedPerson: [''], // İlgili Kişi
      otherVendorAddress: [''], // Adres Tarifi
      paymentType: [''], // Ödeme tipi (MASRAF için)
      selectedCard: [''], // Seçilen kart (Kredi Kartı seçiliyse)
      details: this.fb.array([])
    });
  }
  /**
   * Diğer Satıcı Vergi/TCKN alanı için doğrulama fonksiyonu.
   *  - 11 haneli ise TCKN algoritmasına göre kontrol edilir.
   *  - 10 haneli ise vergi numarası kontrolü yapılır.
   *  - 10 tane "1" ise (ör: 1111111111) kontrol yapılmaz (her zaman true).
   * @param value string | null | undefined
   * @returns boolean geçerli ise true, aksi halde false
   */
  validateTaxNumber(value: string | null | undefined): boolean {
    if (!value) return true;
    value = value.trim();
    if (value.length === 10) {
      // Tüm haneler 1 ise kontrol yapmadan true döndür
      if (value === '1111111111') return true;
      // Vergi Numarası Algoritması (Türkiye)
      // Her hane için (ilk 9 hane) özel hesaplama yapılır.
      let sum = 0;
      for (let i = 0; i < 9; i++) {
        let digit = Number(value[i]);
        let tmp = (digit + 10 - (i + 1)) % 10;
        if (tmp !== 0) {
          tmp = (tmp * Math.pow(2, 10 - (i + 1))) % 9;
          if (tmp === 0) tmp = 9;
        }
        sum += tmp;
      }
      let lastDigit = (10 - (sum % 10)) % 10;
      return Number(value[9]) === lastDigit;
    } else if (value.length === 11) {
      // TCKN Algoritması
      if (!/^\d{11}$/.test(value)) return false;
      if (value[0] === '0') return false; // Başında 0 olamaz
      let digits = value.split('').map(Number);
      const totalOdd = digits[0] + digits[2] + digits[4] + digits[6] + digits[8];
      const totalEven = digits[1] + digits[3] + digits[5] + digits[7];
      const digit10 = ((totalOdd * 7) - totalEven) % 10;
      const digit11 = (digits.slice(0, 10).reduce((a, b) => a + b, 0)) % 10;
      return digits[9] === ((digit10 + 10) % 10) && digits[10] === digit11;
    }
    return false;
  }

  ngOnInit(): void {
    // Readonly mode kontrolü
    if (this.data?.isReadOnly) {
      this.isReadOnly = true;
    }
    
    // Onay/Red butonlarını göster/gizle
    if (this.data?.showApproveReject) {
      this.showApproveReject = true;
    }
    
    this.loadProcurementGroups();
    this.loadDepartments();
    this.loadProjects();
    this.loadTenants();
    this.loadPaymentTypes();
    this.loadCreditCards();
    this.loadUnitTypes();
    
    // PG (Satınalma Üst Grubu) değiştiğinde PGD listesini yükle
    this.requestForm.get('pgCode')?.valueChanges.subscribe(pgValue => {
      if (pgValue && pgValue.trim() !== '') {
        this.onPgChange(pgValue);
      }
    });
    
    // PGD (Satınalma Alt Grubu) değiştiğinde WG listesini yükle
    this.requestForm.get('pgdCode')?.valueChanges.subscribe(pgdValue => {
      if (pgdValue && pgdValue.trim() !== '') {
        this.onPgdChange(pgdValue);
      }
    });
    
    // Vendor search - 3 karakter yazıldığında arama yap
    const vendorSearchControl = this.requestForm.get('vendorSearch');
    if (vendorSearchControl) {
      vendorSearchControl.valueChanges
        .pipe(
          debounceTime(300),
          distinctUntilChanged()
        )
        .subscribe(searchTerm => {
          // Sadece string olan değerleri işle (object seçimleri ignore et)
          if (typeof searchTerm === 'string') {
            if (searchTerm && searchTerm.length >= 3) {
              this.loadVendors(searchTerm);
            } else {
              // 3 karakterden az ise sadece "Diğer" göster
              const otherOption = { id: 0, name: 'Diğer', taxNumber: '' } as Vendor;
              this.vendorList = [otherOption];
            }
          }
        });
    }
    
    if (this.data?.request) {
      this.recordType = (this.data.request.type === 'EXPENSE' ? 'EXPENSE' : 'REQUISITION');
      
      // Form değerlerini set et
      this.requestForm.patchValue({
        type: this.data.request.type,
        requestHeader: this.data.request.requestHeader,
        explanation: this.data.request.explanation,
        invoiceDate: this.data.request.invoiceDate,
        pgCode: this.data.request.pgCode,
        pgdCode: this.data.request.pgdCode,
        wgCode: this.data.request.wgCode,
        pgCodeDesc: this.data.request.pgCodeDesc,
        pgdCodeDesc: this.data.request.pgdCodeDesc,
        wgCodeDesc: this.data.request.wgCodeDesc,
        vendorId: this.data.request.vendorId,
        otherVendorTaxNumber: this.data.request.otherVendorTaxNumber,
        otherVendorName: this.data.request.otherVendorName,
        // TALEP Satıcı Önerisi alanları
        isVendorDefined: this.data.request.isVendorDefined !== undefined ? this.data.request.isVendorDefined : true,
        otherVendorPhone: this.data.request.otherVendorPhone,
        otherVendorRelatedPerson: this.data.request.otherVendorRelatedPerson,
        otherVendorAddress: this.data.request.otherVendorAddress,
        paymentType: this.data.request.paymentType || '',
        selectedCard: this.data.request.selectedCard || ''
      });
      
      // PGD ve WG listelerini yükle (asenkron olduğu için setTimeout ile)
      setTimeout(() => {
        if (this.data?.request?.pgCode) {
          this.onPgChange(this.data.request.pgCode);
          setTimeout(() => {
            if (this.data?.request?.pgdCode) {
              this.requestForm.patchValue({ pgdCode: this.data.request.pgdCode });
              this.onPgdChange(this.data.request.pgdCode);
              setTimeout(() => {
                if (this.data?.request?.wgCode) {
                  this.requestForm.patchValue({ wgCode: this.data.request.wgCode });
                  // WG set edildikten sonra items'ı yükle
                  this.loadProcurementItems();
                }
              }, 100);
            }
          }, 100);
        }
      }, 100);
      
      // Details'i ekle
      this.data.request.details.forEach(detail => this.addDetail(detail));
      
      // MASRAF ve TALEP için farklı vendor kontrolleri
      if (this.recordType === 'EXPENSE') {
        // MASRAF için eski vendor mantığı
        if (this.data.request.vendorId) {
          // Vendor ID var - normal vendor seçimi
          this.showOtherVendorFields = false;
          
          const vendorName = (this.data.request as any).vendorName || this.data.request.otherVendorName || '';
          const vendorTaxNumber = (this.data.request as any).vendorTaxNumber || this.data.request.otherVendorTaxNumber || '';
          
          const vendorObj = {
            id: this.data.request.vendorId,
            name: vendorName,
            taxNumber: vendorTaxNumber
          } as Vendor;
          this.selectedVendor = vendorObj;
          if (!this.isReadOnly && this.vendorList.findIndex(v => v.id === vendorObj.id) === -1) {
            this.vendorList.unshift(vendorObj);
          }
          this.requestForm.patchValue({
            vendorSearch: vendorObj
          });
        } else if (this.data.request.otherVendorTaxNumber) {
          // "Diğer" seçilmiş
          this.showOtherVendorFields = true;
          this.selectedVendor = { id: 0, name: 'Diğer', taxNumber: '' } as Vendor;
          this.requestForm.patchValue({
            vendorSearch: 'OTHER'
          });
        }
      } else {
        // TALEP için Satıcı Önerisi mantığı
        if (this.data.request.otherVendorName !== null && 
            this.data.request.otherVendorName !== undefined && 
            this.data.request.otherVendorName.trim() !== '') {
          // Manuel satıcı önerisi girilmiş - isVendorDefined = false
          this.requestForm.patchValue({
            isVendorDefined: false
          });
        } else if (this.data.request.vendorId) {
          // Tanımlı satıcı seçilmiş - isVendorDefined = true
          this.requestForm.patchValue({
            isVendorDefined: true
          });
          
          const vendorName = (this.data.request as any).vendorName || '';
          const vendorTaxNumber = (this.data.request as any).vendorTaxNumber || '';
          
          const vendorObj = {
            id: this.data.request.vendorId,
            name: vendorName,
            taxNumber: vendorTaxNumber
          } as Vendor;
          this.selectedVendor = vendorObj;
          if (!this.isReadOnly && this.vendorList.findIndex(v => v.id === vendorObj.id) === -1) {
            this.vendorList.unshift(vendorObj);
          }
          this.requestForm.patchValue({
            vendorSearch: vendorObj
          });
        }
      }
    } else {
      // Yeni kayıt - dropdown göster
      this.showOtherVendorFields = false;
      this.selectedVendor = null;
      
      // Create modda "Diğer" seçeneğini varsayılan olarak ekle
      const otherOption = { id: 0, name: 'Diğer', taxNumber: '' } as Vendor;
      this.vendorList = [otherOption];
    }
    this.updateDataSource();
    this.updateColumnsBasedOnType();
    
    // Readonly modda tüm form alanlarını disable et
    if (this.isReadOnly) {
      this.requestForm.disable();
    }
    
    // Type değiştiğinde kolonları güncelle ve invoiceDate validasyonunu ayarla
    this.requestForm.get('type')?.valueChanges.subscribe(type => {
      this.recordType = (type === 'EXPENSE' ? 'EXPENSE' : 'REQUISITION');
      this.updateColumnsBasedOnType();
      this.updateInvoiceDateValidation();
    });
    
    // İlk yüklemede invoiceDate validasyonunu ayarla
    this.updateInvoiceDateValidation();
    
    // PG değiştiğinde PGD listesini güncelle
    this.requestForm.get('pgCode')?.valueChanges.subscribe(pgValue => {
      this.onPgChange(pgValue);
    });
    
    // PGD değiştiğinde WG listesini güncelle
    this.requestForm.get('pgdCode')?.valueChanges.subscribe(pgdValue => {
      this.onPgdChange(pgdValue);
    });
    
    // WG değiştiğinde item listesini yükle
    this.requestForm.get('wgCode')?.valueChanges.subscribe(wgValue => {
      this.loadProcurementItems();
    });
    
    // isVendorDefined değiştiğinde satıcı alanlarının validasyonunu ayarla
    this.requestForm.get('isVendorDefined')?.valueChanges.subscribe(isDefined => {
      this.updateVendorFieldsValidation(isDefined);
    });

    // paymentType değiştiğinde Kredi Kartı değilse selectedCard temizle
    this.requestForm.get('paymentType')?.valueChanges.subscribe(() => {
      if (!this.isCreditCardSelected()) {
        this.requestForm.patchValue({ selectedCard: '' }, { emitEvent: false });
      }
    });
    
    // İlk yüklemede validasyonu ayarla
    this.updateVendorFieldsValidation(this.requestForm.get('isVendorDefined')?.value);
  }
  
  updateColumnsBasedOnType(): void {
    if (this.recordType === 'EXPENSE') {
      this.displayedColumns = ['itemCode', 'itemDesc', 'brand', 'quantity', 'unitPrice', 'unit', 'location', 'tenant', 'vatRate', 'amount', 'vatAmount', 'actions'];
    } else {
      // TALEP (REQUISITION) için productLink kolonu ekle
      this.displayedColumns = ['itemCode', 'itemDesc', 'productLink', 'brand', 'quantity', 'unit', 'location', 'tenant', 'actions'];
    }
  }
  
  // İş günü hesaplama (hafta sonu ve tatiller hariç)
  calculateBusinessDays(date1: Date, date2: Date): number {
    const startDate = new Date(date1);
    const endDate = new Date(date2);
    let businessDays = 0;
    
    const current = new Date(startDate);
    while (current <= endDate) {
      const dayOfWeek = current.getDay();
      // Hafta sonu değilse (0=Pazar, 6=Cumartesi)
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        businessDays++;
      }
      current.setDate(current.getDate() + 1);
    }
    
    return businessDays;
  }
  
  // Satıcı alanlarının validasyonunu güncelle
  updateVendorFieldsValidation(isDefined: boolean): void {
    const otherVendorNameControl = this.requestForm.get('otherVendorName');
    const otherVendorPhoneControl = this.requestForm.get('otherVendorPhone');
    const otherVendorRelatedPersonControl = this.requestForm.get('otherVendorRelatedPerson');
    const otherVendorAddressControl = this.requestForm.get('otherVendorAddress');
    
    if (isDefined === false) {
      // Satıcı tanımlı değil - manuel giriş alanları zorunlu
      otherVendorNameControl?.setValidators([Validators.required]);
      otherVendorPhoneControl?.setValidators([Validators.required]);
      otherVendorRelatedPersonControl?.setValidators([Validators.required]);
      otherVendorAddressControl?.setValidators([Validators.required]);
    } else {
      // Satıcı tanımlı - validatorları kaldır
      otherVendorNameControl?.clearValidators();
      otherVendorPhoneControl?.clearValidators();
      otherVendorRelatedPersonControl?.clearValidators();
      otherVendorAddressControl?.clearValidators();
    }
    
    // Değişiklikleri uygula
    otherVendorNameControl?.updateValueAndValidity();
    otherVendorPhoneControl?.updateValueAndValidity();
    otherVendorRelatedPersonControl?.updateValueAndValidity();
    otherVendorAddressControl?.updateValueAndValidity();
  }
  
  // Fatura tarihi validasyonu (30 iş günü öncesine izin verme)
  updateInvoiceDateValidation(): void {
    const invoiceDateControl = this.requestForm.get('invoiceDate');
    if (!invoiceDateControl) return;
    
    if (this.recordType === 'EXPENSE') {
      // MASRAF için zorunlu ve 30 iş günü kontrolü
      invoiceDateControl.setValidators([
        Validators.required,
        (control) => {
          if (!control.value) return null;
          
          const selectedDate = new Date(control.value);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          selectedDate.setHours(0, 0, 0, 0);
          
          if (selectedDate > today) {
            return { futureDate: true };
          }
          
          const businessDays = this.calculateBusinessDays(selectedDate, today);
          if (businessDays > 30) {
            return { exceeds30BusinessDays: true };
          }
          
          return null;
        }
      ]);
    } else {
      // TALEP için zorunlu değil
      invoiceDateControl.clearValidators();
    }
    invoiceDateControl.updateValueAndValidity();
  }
  
  loadProcurementGroups(): void {
    this.procurementService.getProcurementGroups().subscribe({
      next: (response) => {
        console.log('Full API Response:', response);
        console.log('Items array:', response?.responseData?.items);
        console.log('Items length:', response?.responseData?.items?.length);
        
        if (response.success && response.responseData.items) {
          // Level 0'daki tüm elemanları al (12 PG grubu)
          this.allGroups = response.responseData.items;
          this.pgGroups = response.responseData.items;
          
          console.log('pgGroups assigned:', this.pgGroups);
          console.log('pgGroups length:', this.pgGroups.length);
          console.log('First pgGroup:', this.pgGroups[0]);
        } else {
          console.error('Response validation failed:', response);
        }
      },
      error: (error) => {
        console.error('Error loading procurement groups:', error);
      }
    });
  }
  
  loadDepartments(): void {
    this.lookupService.listByLookupCode('DEPARTMENT').subscribe({
      next: (response) => {
        if (response.success && response.responseData.items) {
          this.departmentList = response.responseData.items
            .filter(dept => !dept.parentLookup || dept.parentLookup === null)
            .sort((a, b) => a.description.toLowerCase().localeCompare(b.description.toLowerCase(), 'tr'));
        }
      },
      error: (error) => {
        console.error('Error loading departments:', error);
      }
    });
  }
  
  loadProjects(): void {
    this.lookupService.listByLookupCode('PROJECT').subscribe({
      next: (response) => {
        if (response.success && response.responseData.items) {
          this.projectList = response.responseData.items.sort((a, b) => 
            a.description.toLowerCase().localeCompare(b.description.toLowerCase(), 'tr')
          );
        }
      },
      error: (error) => {
        console.error('Error loading projects:', error);
      }
    });
  }
  
  loadTenants(): void {
    this.lookupService.listByLookupCode('TENANT').subscribe({
      next: (response) => {
        if (response.success && response.responseData.items) {
          this.tenantList = response.responseData.items.sort((a, b) => 
            a.description.toLowerCase().localeCompare(b.description.toLowerCase(), 'tr')
          );
        }
      },
      error: (error) => {
        console.error('Error loading tenants:', error);
      }
    });
  }

  loadPaymentTypes(): void {
    this.lookupService.listByLookupCode('PO_PAYMENT_TYPE').subscribe({
      next: (response) => {
        if (response.success && response.responseData.items) {
          this.paymentTypeList = response.responseData.items.sort((a, b) =>
            (a.description || '').toLowerCase().localeCompare((b.description || '').toLowerCase(), 'tr')
          );
        }
      },
      error: (error) => {
        console.error('Error loading payment types:', error);
      }
    });
  }

  loadCreditCards(): void {
    this.lookupService.listByLookupCode('PO_CREDIT_CARDS').subscribe({
      next: (response) => {
        if (response.success && response.responseData.items) {
          this.creditCardList = response.responseData.items.sort((a, b) =>
            (a.description || '').toLowerCase().localeCompare((b.description || '').toLowerCase(), 'tr')
          );
        }
      },
      error: (error) => {
        console.error('Error loading credit cards:', error);
      }
    });
  }

  loadUnitTypes(): void {
    this.lookupService.listByLookupCode('UNIT_TYPE').subscribe({
      next: (response) => {
        if (response.success && response.responseData.items) {
          this.unitTypeList = response.responseData.items.sort((a, b) =>
            (a.description || '').toLowerCase().localeCompare((b.description || '').toLowerCase(), 'tr')
          );
        }
      },
      error: (error) => {
        console.error('Error loading unit types:', error);
      }
    });
  }

  /** Kredi Kartı seçili mi? (selectedCard dropdown gösterimi için) */
  isCreditCardSelected(): boolean {
    const paymentType = this.requestForm.get('paymentType')?.value;
    if (!paymentType) return false;
    const selected = this.paymentTypeList.find(p => p.lookupValue === paymentType);
    if (!selected) return false;
    // description "Kredi Kartı" veya "Kredi Kartı seçiniz" olabilir; lookupValue da "Kredi Kartı" olabilir
    const desc = (selected.description || '').toLowerCase();
    const lookup = (selected.lookupValue || '').toLowerCase();
    return desc.includes('kredi kartı') || lookup.includes('kredi');
  }

  /** Readonly modda Ödeme Tipi görüntüleme */
  getPaymentTypeDisplay(): string {
    const paymentType = this.requestForm.get('paymentType')?.value;
    if (!paymentType) return '-';
    const item = this.paymentTypeList.find(p => p.lookupValue === paymentType);
    return item ? `${item.lookupValue} - ${item.description}` : paymentType;
  }

  /** Readonly modda Kredi Kartı görüntüleme */
  getSelectedCardDisplay(): string {
    const selectedCard = this.requestForm.get('selectedCard')?.value;
    if (!selectedCard) return '-';
    const item = this.creditCardList.find(c => c.lookupValue === selectedCard);
    return item ? `${item.lookupValue} - ${item.description}` : selectedCard;
  }
  
  // Proje/Lokasyon description'ını filtrele (PR/KİR/KAR varsa tire öncesini gösterme)
  formatProjectTenantDescription(description: string): string {
    if (!description) return '';
    
    // Tire varsa ayır
    if (description.includes('-')) {
      const parts = description.split('-');
      const beforeDash = parts[0].trim().toUpperCase();
      
      // PR, KİR, KAR içeriyorsa sadece tire sonrasını göster
      if (beforeDash.includes('PR') || beforeDash.includes('KİR') || beforeDash.includes('KAR') || 
          beforeDash.includes('KIR')) {
        return parts.slice(1).join('-').trim();
      }
    }
    
    return description;
  }
  
  loadVendors(searchTerm: string): void {
    this.procurementService.getVendors(searchTerm).subscribe({
      next: (response) => {
        if (response.success && response.responseData.items) {
          // Arama sonuçlarını al
          const searchResults = response.responseData.items;
          
          // "Diğer" seçeneğini en başa ekle
          const otherOption = { id: 0, name: 'Diğer', taxNumber: '' } as Vendor;
          this.vendorList = [otherOption, ...searchResults];
        }
      },
      error: (error) => {
        console.error('Error loading vendors:', error);
        // Hata durumunda sadece "Diğer" göster
        const otherOption = { id: 0, name: 'Diğer', taxNumber: '' } as Vendor;
        this.vendorList = [otherOption];
      }
    });
  }
  
  onVendorSearch(event: any): void {
    const searchTerm = event.target?.value || '';
    
    if (searchTerm && searchTerm.length >= 3) {
      this.loadVendors(searchTerm);
    } else {
      this.vendorList = [];
    }
  }
  
  onVendorSelect(vendor: Vendor | string): void {
    if (vendor === 'OTHER' || (typeof vendor === 'object' && vendor.id === 0)) {
      // "Diğer" seçildi
      this.showOtherVendorFields = true;
      this.selectedVendor = null;
      this.requestForm.patchValue({ 
        vendorId: null,
        vendorSearch: 'OTHER'
      }, { emitEvent: false }); // valueChanges tetiklenmemesi için
    } else if (typeof vendor === 'object') {
      // Vendor seçildi
      this.showOtherVendorFields = false;
      this.selectedVendor = vendor;
      this.requestForm.patchValue({
        vendorId: vendor.id,
        vendorSearch: vendor,
        otherVendorTaxNumber: '',
        otherVendorName: ''
      }, { emitEvent: false }); // valueChanges tetiklenmemesi için
    }
  }
  
  displayVendor = (vendor: Vendor | string | null): string => {
    if (!vendor) {
      return '';
    }
    if (vendor === 'OTHER') {
      return 'Diğer';
    }
    if (typeof vendor === 'string') {
      return vendor;
    }
    // "Diğer" seçeneği için tire gösterme (id === 0)
    if (vendor.id === 0) {
      return vendor.name;
    }
    // taxNumber undefined ise sadece name göster
    if (vendor.taxNumber) {
      return `${vendor.name} - ${vendor.taxNumber}`;
    }
    return vendor.name || '';
  }
  
  onProjectChange(projectCode: string, index: number): void {
    const detailGroup = this.details.at(index) as FormGroup;
    if (projectCode !== 'TENANT') {
      // Kiracı seçilmediyse tenantCode'u temizle
      detailGroup.patchValue({ tenantCode: '' });
    }
  }
  
  isTenantVisible(index: number): boolean {
    const detailGroup = this.details.at(index) as FormGroup;
    return detailGroup?.get('projectCode')?.value === 'TENANT';
  }
  
  isAnyTenantVisible(): boolean {
    return this.details.controls.some((control, index) => this.isTenantVisible(index));
  }
  
  onPgChange(pgValue: string): void {
    const selectedPg = this.pgGroups.find(pg => pg.value === pgValue);
    
    if (selectedPg?.children && selectedPg.children.length > 0) {
      this.pgdGroups = selectedPg.children;
      this.requestForm.get('pgdCode')?.enable();
    } else {
      this.pgdGroups = [];
      this.requestForm.get('pgdCode')?.disable();
    }
    
    // PGD ve WG seçimlerini temizle
    this.requestForm.patchValue({
      pgdCode: '',
      wgCode: ''
    });
    this.wgGroups = [];
  }
  
  onPgdChange(pgdValue: string): void {
    const selectedPgd = this.pgdGroups.find(pgd => pgd.value === pgdValue);
    
    if (selectedPgd?.children && selectedPgd.children.length > 0) {
      this.wgGroups = selectedPgd.children;
      this.requestForm.get('wgCode')?.enable();
    } else {
      this.wgGroups = [];
      this.requestForm.get('wgCode')?.disable();
    }
    
    // WG seçimini temizle
    this.requestForm.patchValue({
      wgCode: ''
    });
    this.procurementItems = [];
  }
  
  loadProcurementItems(): void {
    const pgCode = this.requestForm.get('pgCode')?.value;
    const pgdCode = this.requestForm.get('pgdCode')?.value;
    const wgCode = this.requestForm.get('wgCode')?.value;
    
    if (pgCode && pgdCode && wgCode) {
      this.procurementService.getProcurementItems({ pgCode, pgdCode, wgCode }).subscribe({
        next: (response) => {
          if (response.success && response.responseData.items) {
            this.procurementItems = response.responseData.items;
            // Items yüklendikten sonra detail descriptions'ları doldur
            this.populateDetailDescriptions();
          }
        },
        error: (error) => {
          console.error('Error loading procurement items:', error);
        }
      });
    }
  }

  populateDetailDescriptions(): void {
    this.data.request?.details.forEach((detail, index) => {
      const formGroup = this.details.at(index) as FormGroup;
      
      // Item description ve itemId'yi güncelle (procurementItems yüklendikten sonra)
      const item = this.procurementItems.find(i => i.id === detail.itemId);
      if (item) {
        formGroup.patchValue({
          itemId: item.id, // Ensure itemId is set with actual value
          itemName: item.name || '' // Ürün/Hizmet Açıklaması
        });
      }
      
      // Department description
      const dept = this.departmentList.find(d => d.id === detail.departmentId);
      if (dept) {
        formGroup.patchValue({ departmentDesc: `${dept.lookupValue} - ${dept.description}` });
      }
      
      // Project description
      if (detail.projectCode === 'TENANT') {
        formGroup.patchValue({ projectDesc: 'Kiracı' });
      } else {
        const project = this.projectList.find(p => p.lookupValue === detail.projectCode);
        if (project) {
          formGroup.patchValue({ projectDesc: `${project.lookupValue} - ${project.description}` });
        }
      }
      
      // Tenant description
      if (detail.tenantCode) {
        const tenant = this.tenantList.find(t => t.lookupValue === detail.tenantCode);
        if (tenant) {
          formGroup.patchValue({ tenantDesc: `${tenant.lookupValue} - ${tenant.description}` });
        }
      }
    });
  }

  get details(): FormArray {
    return this.requestForm.get('details') as FormArray;
  }

  updateDataSource(): void {
    this.dataSource = [...this.details.controls];
  }

  createDetailFormGroup(detail?: RequestDetail): FormGroup {
    const formGroup = this.fb.group({
      id: [detail?.id],
      detailNo: [detail?.detailNo],
      itemId: [detail?.itemId],
      itemName: [detail?.itemName || ''], // Ürün/Hizmet Açıklaması
      itemDesc: [detail?.itemDesc || ''], // Ürün/Hizmet Serbet Açıklama
      productLink: [detail?.productLink || ''], // Ürün/Hizmet Linki
      brandDesc: [detail?.brandDesc || ''],
      departmentId: [detail?.departmentId || null, Validators.required],
      departmentDesc: [detail?.departmentDesc || ''], // Readonly için department description
      projectCode: [detail?.projectCode || '', Validators.required],
      projectDesc: [detail?.projectDesc || ''], // Readonly için project description
      tenantCode: [detail?.tenantCode || ''],
      tenantDesc: [detail?.tenantDesc || ''], // Readonly için tenant description
      quantity: [detail?.quantity || 1, [Validators.required, Validators.min(1)]],
      uomDesc: [detail?.uomDesc || 'ADET'],
      unitPrice: [detail?.unitPrice ?? (detail?.quantity && detail?.amount ? detail.amount / detail.quantity : 0)],
      amount: [detail?.amount || 0],
      vatRate: [detail?.vatRate || 0],
      vatAmount: [detail?.vatAmount || 0]
    });
    
    // MASRAF için: amount = unitPrice * quantity, vatAmount = amount * vatRate/100 (her satırda abone ol)
    formGroup.get('unitPrice')?.valueChanges.subscribe(() => this.calculateAmountAndVat(formGroup));
    formGroup.get('quantity')?.valueChanges.subscribe(() => this.calculateAmountAndVat(formGroup));
    formGroup.get('vatRate')?.valueChanges.subscribe(() => this.calculateAmountAndVat(formGroup));
    
    return formGroup;
  }
  
  /** MASRAF için: amount = unitPrice * quantity, vatAmount = amount * vatRate/100 */
  calculateAmountAndVat(detailGroup: FormGroup): void {
    const unitPrice = detailGroup.get('unitPrice')?.value || 0;
    const quantity = detailGroup.get('quantity')?.value || 0;
    const vatRate = detailGroup.get('vatRate')?.value || 0;
    const amount = unitPrice * quantity;
    const vatAmount = (amount * vatRate) / 100;
    detailGroup.get('amount')?.setValue(amount, { emitEvent: false });
    detailGroup.get('vatAmount')?.setValue(vatAmount, { emitEvent: false });
  }

  addDetail(detail?: RequestDetail): void {
    const newDetail = this.createDetailFormGroup(detail);
    // DetailNo otomatik ata (1'den başlar)
    if (!detail?.detailNo) {
      newDetail.patchValue({ detailNo: this.details.length + 1 });
    }
    this.details.push(newDetail);
    this.updateDataSource();
  }
  
  onItemSelect(itemId: number, index: number): void {
    const selectedItem = this.procurementItems.find(item => item.id === itemId);
    if (selectedItem) {
      const detailGroup = this.details.at(index) as FormGroup;
      const unitValue = this.resolveUnitType(selectedItem.baseUnitOfMeasure);
      detailGroup.patchValue({
        itemId: selectedItem.id,
        itemName: selectedItem.name || '',
        uomDesc: unitValue
      });
    }
  }

  /**
   * baseUnitOfMeasure değerini UNIT_TYPE lookup listesinde arar.
   * Eşleşme varsa o lookupValue'yu, yoksa 'ADET' döndürür.
   */
  private resolveUnitType(baseUnit: string | null | undefined): string {
    if (!baseUnit) return 'ADET';
    const match = this.unitTypeList.find(u =>
      (u.lookupValue || '').toUpperCase() === baseUnit.toUpperCase() ||
      (u.description || '').toUpperCase() === baseUnit.toUpperCase()
    );
    return match ? match.lookupValue : 'ADET';
  }

  /** Seçili ürünün birim ölçüsünü (baseUnitOfMeasure) döndürür */
  getBaseUnitForRow(row: FormGroup): string {
    const itemId = row.get('itemId')?.value;
    if (!itemId) return '';
    const item = this.procurementItems.find(i => i.id === itemId);
    return item?.baseUnitOfMeasure || '';
  }

  removeDetail(index: number): void {
    this.details.removeAt(index);
    // DetailNo'ları yeniden numaralandır
    this.details.controls.forEach((control, i) => {
      control.patchValue({ detailNo: i + 1 });
    });
    this.updateDataSource();
  }

  onSave(): void {
    if (this.requestForm.valid) {
      const request = this.buildRequestFromForm();
      if (!request) return;

      // İkinci kez kayda basıldığında validasyon atlanır
      if (this.skipHeaderValidation) {
        this.doSaveProcurement(request);
        return;
      }

      // İlk kayıtta önce Talep Başlığı AI validasyonu
      this.procurementService.validateRequestHeader(request).subscribe({
        next: (response) => {
          // API responseData.item veya responseData veya doğrudan response olabilir
          const validation = response.responseData?.item ?? response.responseData ?? response;
          const suitable = validation?.suitable;
          const suggestion = validation?.suggestion;
          if (suitable) {
            this.headerValidationSuggestion = null;
            this.doSaveProcurement(request);
          } else {
            // suggestion boş değilse API'den gelen mesajı göster
            this.headerValidationSuggestion = (suggestion && suggestion.trim() !== '')
              ? suggestion
              : 'Talep başlığını daha açıklayıcı hale getirmeniz önerilir.';
            this.skipHeaderValidation = true;
            this.cdr.detectChanges();
          }
        },
        error: (error) => {
          console.error('Validation error:', error);
          // Validasyon hatası durumunda yine de kayda izin ver
          this.doSaveProcurement(request);
        }
      });
    } else {
      alert('Lütfen tüm zorunlu alanları doldurun!');
    }
  }

  private buildRequestFromForm(): Request | null {
    const formValue = this.requestForm.value;

    const details = formValue.details.map((detail: any, index: number) => ({
      id: null,
      detailNo: detail.detailNo || (index + 1),
      itemId: detail.itemId || null,
      itemDesc: detail.itemDesc || '',
      productLink: detail.productLink || '',
      brandDesc: detail.brandDesc || '',
      departmentId: detail.departmentId || null,
      projectCode: detail.projectCode || '',
      tenantCode: detail.tenantCode || null,
      quantity: detail.quantity || 0,
      uomDesc: detail.uomDesc || 'ADET',
      unitPrice: detail.unitPrice ?? 0,
      amount: detail.amount || 0,
      vatRate: detail.vatRate || null,
      vatAmount: detail.vatAmount || 0
    }));

    const request: Request = {
      id: null,
      type: formValue.type || 'REQUISITION',
      requestHeader: formValue.requestHeader || '',
      explanation: formValue.explanation || '',
      invoiceDate: formValue.invoiceDate || null,
      pgCode: formValue.pgCode || '',
      pgdCode: formValue.pgdCode || '',
      wgCode: formValue.wgCode || '',
      details: details
    };

    if (formValue.type === 'EXPENSE') {
      if (formValue.vendorId) {
        request.vendorId = formValue.vendorId;
      } else {
        request.otherVendorTaxNumber = formValue.otherVendorTaxNumber || null;
        request.otherVendorName = formValue.otherVendorName || null;
      }
      request.paymentType = formValue.paymentType || null;
      request.selectedCard = formValue.selectedCard || null;
    } else {
      request.isVendorDefined = formValue.isVendorDefined;
      if (formValue.isVendorDefined === true) {
        request.vendorId = formValue.vendorId;
      } else {
        request.otherVendorName = formValue.otherVendorName || null;
        request.otherVendorPhone = formValue.otherVendorPhone || null;
        request.otherVendorRelatedPerson = formValue.otherVendorRelatedPerson || null;
        request.otherVendorAddress = formValue.otherVendorAddress || null;
      }
    }

    return request;
  }

  private doSaveProcurement(request: Request): void {
    this.procurementService.saveProcurement(request).subscribe({
      next: (response) => {
        if (response.success) {
          this.dialogRef.close({ success: true, data: response.responseData });
        } else {
          console.error('Save failed:', response.msg);
          alert(response.msg || 'Kaydetme başarısız: Bilinmeyen hata');
        }
      },
      error: (error) => {
        console.error('Save error:', error);
        const errorMessage = error?.error?.msg || error?.message || 'Kaydetme sırasında hata oluştu!';
        alert(errorMessage);
      }
    });
  }
  
  calculateTotal(): number {
    let total = 0;
    this.details.controls.forEach(control => {
      const amount = control.get('amount')?.value || 0;
      const vatAmount = control.get('vatAmount')?.value || 0;
      total += amount + vatAmount;
    });
    return total;
  }
  
  getTotalAmount(): number {
    return this.calculateTotal();
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onApprove(): void {
    console.log('onApprove called, data.request:', this.data.request);
    console.log('data.request.id:', this.data.request?.id);
    
    const currentUser = this.authService.currentUserValue;
    const request = {
      employeeId: currentUser.employeeId,
      operationRecordId: this.data.request?.id,
      operationTableName: 'PURCHASE_ORDER'
    };
    
    this.procurementService.approveRequest(request).subscribe({
      next: (result) => {
        this.dialogRef.close({ approved: true });
      },
      error: (error) => {
        console.error('Approve error:', error);
        alert('Onaylama sırasında hata oluştu!');
      }
    });
  }

  onReject(): void {
    // Red nedeni dialog'unu aç
    const dialogRef = this.dialog.open(RejectReasonDialogComponent, {
      width: '500px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const currentUser = this.authService.currentUserValue;
        const request = {
          employeeId: currentUser.employeeId,
          operationRecordId: this.data.request?.id,
          operationTableName: 'PURCHASE_ORDER',
          rejectReasonId: result.rejectReasonId,
          rejectDescription: result.rejectDescription
        };

        this.procurementService.rejectRequest(request).subscribe({
          next: (response) => {
            this.dialogRef.close({ rejected: true });
          },
          error: (error) => {
            console.error('Reject error:', error);
            const errorMessage = error?.error?.msg || error?.message || 'Reddetme sırasında hata oluştu!';
            alert(errorMessage);
          }
        });
      }
    });
  }
}